// janus-engine/rolls/random-table-outcome.ts — Lane N2 RT-4 (2026-10-01): the browser twin of
// apply_contingent_or_random_table_ability() (includes/functions/janus/core/apply_contingent_or_random_table_ability.php) and of the
// two places Janus calls it (janus.php ~1729 for an UNCONTESTED ability, the deferred call at the end of damage_roll() for a
// CONTESTED one — damage_roll.php ~1347). PURE: it decides; the ability engine (janus-engine/ability/**, Lane A) applies the decision
// with its own applyStatusEffect / insertRoll, and the ability commit records it. Nothing here touches ctx.
//
// Rules carried over verbatim (owner rulings recorded in the PHP header):
//   - `contingent_ability` (a fixed outcome id) wins over `roll_random_table`; only the table ROLLS (rollRandomTable, random-table.ts).
//   - An outcome that is a STATUS_EFFECT or PASSIVE_ABILITY lands on the ability's creature TARGETS — on the targets the contest LANDED
//     on when the ability resolves as a contest; an empty landed list means everyone resisted → NO ONE (never the caster). With no
//     contest and no creature target (self-cast / GM roll) it falls back to the CASTER.
//     Duration source: a status_effect governs its OWN duration; a passive_ability takes the PARENT's ("duration set by the preceding
//     ability"). has_duration = the source carries both duration_int and duration_type.
//   - An outcome in an ABILITY bucket is GRANTED to the caster (status_item_id = outcome, status_origin_id = parent), TTL from the
//     parent, has_duration always true; the contest never gates it.
//   - Any other outcome type (or none, or an unreadable outcome) applies nothing — the roll rows are still logged.
//   - Timing: uncontested → at dispatch (before the contest would roll); contested → after the contest, with the landed list.

import type { RandomTableRollResult, RandomTableRollStep } from './random-table.js';

type Row = Record<string, unknown>;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const isObj = (v: unknown): v is Row => !!v && typeof v === 'object' && !Array.isArray(v);

export const RANDOM_TABLE_OUTCOME_ABILITY_TYPES: ReadonlySet<string> = new Set(['ability', 'full_ability', 'quick_ability', 'attack_ability',
  'free_ability', 'reaction_ability', 'opportunity_attack_ability']);
export const RANDOM_TABLE_OUTCOME_STATUS_TYPES: ReadonlySet<string> = new Set(['status_effect', 'passive_ability']);

/** A layer slot's first scalar (descalared scalar, a [value] list, or a shell's item id) — layer_value()'s shape tolerance. */
export function layerScalar(def: unknown, key: string): unknown {
  const ilt = isObj(def) && isObj(def.item_layer_types) ? def.item_layer_types : null;
  let v: unknown = ilt ? ilt[key] : undefined;
  if (Array.isArray(v)) v = v[0];
  if (isObj(v)) v = isObj(v.items) ? v.items.item_id : v.item_id ?? v.item_layer_effect;
  return v;
}
const layerHas = (def: unknown, key: string): boolean => { const v = layerScalar(def, key); return v !== undefined && v !== null && v !== ''; };
const defField = (def: unknown, key: string): unknown => isObj(def) ? (isObj(def.items) ? def.items[key] : undefined) ?? def[key] : undefined;

/** Which payload the parent ability carries (null = neither): `contingent_ability` takes precedence, like the PHP. */
export function randomTablePayloadOf(parent: Row): { kind: 'contingent'; outcomeId: string } | { kind: 'table'; tableId: string } | null {
  const contingent = upper(layerScalar(parent, 'contingent_ability'));
  if (UUID.test(contingent)) return { kind: 'contingent', outcomeId: contingent };
  const table = upper(layerScalar(parent, 'roll_random_table'));
  if (UUID.test(table)) return { kind: 'table', tableId: table };
  return null;
}
/** When the payload resolves: 'dispatch' (uncontested — janus.php) or 'after-contest' (contested — the end of damage_roll). */
export function randomTableOutcomeTiming(parent: Row, resolvesAsContest: boolean): 'dispatch' | 'after-contest' | null {
  return randomTablePayloadOf(parent) ? (resolvesAsContest ? 'after-contest' : 'dispatch') : null;
}

export interface RandomTableOutcomeInput {
  /** The parent ability (item_id_used_arr): carries `roll_random_table` / `contingent_ability`, duration layers, its item id. */
  parent: Row;
  /** The browser's roll on the parent's table (rollRandomTable(...).result) — required when the payload is a table, ignored otherwise. */
  rolled: RandomTableRollResult | null;
  /** The outcome's definition, when held (the definition store) — its item_type routes the outcome and may carry its duration. */
  outcomeDef: Row | null;
  /** The bucket's attached_type for the rolled outcome (random-table.ts), used when the definition is not held yet. */
  outcomeTypeHint?: string | null;
  casterId: string;
  /** The creature targets of the use, in order (non-creature targets already dropped). */
  targetIds: string[];
  /** null = the ability does not resolve as a contest; an array = the targets the contest LANDED on (failed their contest). */
  landedIds: string[] | null;
}
export type RandomTableOutcomeDecision =
  | { kind: 'none'; outcomeId: string | null; reason: 'no-payload' | 'no-outcome' | 'outcome-not-held' | 'unsupported-type' | 'all-resisted' }
  | { kind: 'status'; outcomeId: string; outcomeType: 'status_effect' | 'passive_ability'; originId: string; targetIds: string[];
      /** whose duration layers set status_ends: the outcome itself (status_effect) or the parent (passive_ability) */
      durationSource: 'outcome' | 'parent'; hasDuration: boolean; logRollType: 'status_effect_applied' }
  | { kind: 'grant'; outcomeId: string; outcomeType: string; originId: string; targetId: string; durationSource: 'parent'; hasDuration: true;
      logRollType: 'granted_ability_applied' };
/** The definition a decision needs before it can be applied (outcome-not-held → fetch it through the definition store, then decide again). */
export function randomTableOutcomeNeeds(decision: RandomTableOutcomeDecision): string | null {
  return decision.kind === 'none' && decision.reason === 'outcome-not-held' ? decision.outcomeId : null;
}

export function resolveRandomTableOutcome(input: RandomTableOutcomeInput): RandomTableOutcomeDecision {
  const payload = randomTablePayloadOf(input.parent);
  if (!payload) return { kind: 'none', outcomeId: null, reason: 'no-payload' };
  const outcomeId = payload.kind === 'contingent' ? payload.outcomeId : upper(input.rolled?.attached ?? '');
  if (!UUID.test(outcomeId)) return { kind: 'none', outcomeId: null, reason: 'no-outcome' };
  const heldType = String(defField(input.outcomeDef, 'item_type') ?? '');
  // The bucket hint describes the ROLLED attachment only — never a contingent outcome (Codex review 2026-10-01).
  const type = heldType || (payload.kind === 'table' ? String(input.outcomeTypeHint ?? '') : '');
  if (!type) return { kind: 'none', outcomeId, reason: 'outcome-not-held' };
  const parentId = upper(defField(input.parent, 'item_id'));
  const originId = UUID.test(parentId) ? parentId : '';
  if (RANDOM_TABLE_OUTCOME_STATUS_TYPES.has(type)) {
    // The status needs its definition to apply (and a status_effect's own duration): an unheld one is fetched first.
    if (!heldType) return { kind: 'none', outcomeId, reason: 'outcome-not-held' };
    const durationSource = type === 'passive_ability' ? 'parent' : 'outcome';
    const source = durationSource === 'parent' ? input.parent : input.outcomeDef;
    const hasDuration = layerHas(source, 'duration_int') && layerHas(source, 'duration_type');
    const pool = (input.landedIds ?? input.targetIds).map(upper).filter(id => UUID.test(id));
    let targetIds = pool;
    if (!pool.length) {
      if (input.landedIds !== null) return { kind: 'none', outcomeId, reason: 'all-resisted' };
      targetIds = [upper(input.casterId)];
    }
    return { kind: 'status', outcomeId, outcomeType: type as 'status_effect' | 'passive_ability', originId, targetIds, durationSource, hasDuration,
      logRollType: 'status_effect_applied' };
  }
  if (RANDOM_TABLE_OUTCOME_ABILITY_TYPES.has(type)) {
    return { kind: 'grant', outcomeId, outcomeType: type, originId, targetId: upper(input.casterId), durationSource: 'parent', hasDuration: true,
      logRollType: 'granted_ability_applied' };
  }
  // A nested table / creature was already rolled by the chain (random-table.ts); loot / gear / text outcomes are log-only.
  return { kind: 'none', outcomeId, reason: 'unsupported-type' };
}

/** The log rows the roll writes inside an ability use (random_table_roll(): "to" = the primary creature target, else no one). */
export function randomTableRowsForAbility(rolled: RandomTableRollResult, targetIds: string[]): Array<RandomTableRollStep & { roll_to: string | null }> {
  const to = targetIds.map(upper).find(id => UUID.test(id)) ?? null;
  return rolled.steps.map(step => ({ ...step, roll_to: to }));
}
